import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { memo, useCallback } from "react";

import Tooltipable from "~/components/effects/tooltipable";
import { SkeletonText } from "~/components/forms/SkeletonComponents";
import { formatTimeAgo } from "~/helpers/timeHelpers";

const isNewLeadNotification = (notification) =>
  notification.Type === "lead" && notification.IconType === "lead";

const getIconByType = (notificationType) => {
  const icons = {
    email_inbound: "fa-inbox-in",
    email: "fa-envelope",
    text: "fa-comment",
    video: "fa-video",
    agent_alerts: "fa-comment-alt-exclamation",
    mention: "fa-at",
    job_application: "fa-briefcase",
    lead: "fa-user",
  };

  return icons[notificationType] || "fa-empty-set";
};

const getNotificationIcon = (notification) =>
  isNewLeadNotification(notification)
    ? ["fas", "fa-user"]
    : ["fal", getIconByType(notification.IconType)];

const NotificationItem = memo(
  ({ notification, toggleReadStatus, isUpdating, loadingSkeleton }) => {
    const isNewLead = () => isNewLeadNotification(notification);
    const getUrl = () => {
      const prefix =
        notification.RecruitingStatus !== false ? "recruiter" : "retention";
      const path =
        notification.InboxConversationID && !isNewLead() ? "inbox" : "today";

      let params;
      if (notification.InboxConversationID && !isNewLead()) {
        params = `name=${notification.LeadName}&conversation=${notification.InboxConversationID}&message=${notification.InboxMessageID}`;
      } else if (isMention()) {
        params = `lead=${notification.LeadID}&_t=${notification.TeamID}&tab=${notification.Type}s`;
      } else if (isCandidate()) {
        params = `lead=${notification.LeadID}&_t=${notification.TeamID}`;
      } else if (isNewLead()) {
        params = `lead=${notification.LeadID}&_t=${notification.TeamID}`;
      } else {
        params = `lead=${notification.LeadID}&_t=${notification.TeamID}&activity_id=${notification.ActivityID}`;
      }

      return `/${prefix}/${path}?${params}`;
    };

    const handleLinkClick = useCallback(
      async (e) => {
        e.preventDefault();

        if (!notification.IsRead) {
          await toggleReadStatus(notification, !notification.IsRead);
        }

        window.location.href = getUrl();
      },
      [notification, toggleReadStatus],
    );

    const renderNotificationTitle = () => {
      if (isMention() || isCandidate()) {
        return (
          <>
            {notification.Title} {renderLeadName()}
          </>
        );
      }

      if (isNewLead()) {
        return renderLeadLink(
          notification.Title || `New lead: ${notification.LeadName}`,
        );
      }

      return (
        <>
          {renderLeadName()} {notification.Title}
        </>
      );
    };

    const renderLeadLink = (label) => (
      <a href="#" className="text-dark" onClick={handleLinkClick}>
        {label}
      </a>
    );

    const renderLeadName = () => renderLeadLink(notification.LeadName);

    const isMention = () => notification.IconType === "mention";

    const isCandidate = () => notification.Type === "job_application";

    return (
      <div
        className={classNames(
          "px-3 d-flex list-group-item rounded-0 border-left-0 border-right-0 border-top-0 border-bottom justify-content-between align-items-center",
          { "loading-skeleton": loadingSkeleton },
        )}
      >
        <div className="d-flex align-items-center mr-1">
          <div
            className="bg-light rounded-circle d-flex justify-content-center align-items-center"
            style={{ width: "40px", height: "40px" }}
          >
            {!loadingSkeleton && (
              <FontAwesomeIcon
                icon={getNotificationIcon(notification)}
                size="lg"
                className="text-secondary"
              />
            )}
          </div>
        </div>

        <div className="flex-grow-1 mr-1">
          <div className="d-flex align-items-center text-grey-dark">
            <span className="flex-grow-1 mr-2">
              {loadingSkeleton ? (
                <SkeletonText
                  data-testid="skeleton-text"
                  className="bg-grey-lightest"
                />
              ) : (
                renderNotificationTitle()
              )}
            </span>
            <span className="d-flex align-items-center text-nowrap">
              <span
                className="rounded-circle bg-grey mr-2"
                style={{ width: "4px", height: "4px" }}
              />
              <span className="text-success small">
                {loadingSkeleton ? (
                  <SkeletonText
                    data-testid="skeleton-text"
                    className="bg-grey-lightest"
                  />
                ) : (
                  formatTimeAgo(notification.CreatedAt)
                )}
              </span>
            </span>
          </div>

          <div
            className={
              notification?.IsRead ? "font-weight-normal" : "font-weight-bold"
            }
          >
            {loadingSkeleton ? (
              <SkeletonText
                data-testid="skeleton-text"
                width="250px"
                className="mt-2 bg-grey-lightest"
              />
            ) : (
              notification.Content
            )}
          </div>
        </div>

        {!loadingSkeleton && (
          <Tooltipable
            text={notification.IsRead ? "Mark as unread" : "Mark as read"}
            placement="left"
          >
            <button
              type="button"
              className={classNames("btn btn-link shadow-none p-0 mr-2", {
                "read-button": notification.IsRead,
                "unread-button": !notification.IsRead,
              })}
              onClick={() =>
                toggleReadStatus(notification, !notification.IsRead)
              }
              aria-label={
                notification.IsRead ? "Mark as unread" : "Mark as read"
              }
            >
              {isUpdating ? (
                <FontAwesomeIcon
                  icon="fa-spinner"
                  className="text-secondary"
                  size="xs"
                  spin
                />
              ) : (
                !notification.IsRead && (
                  <FontAwesomeIcon
                    icon="fa-circle"
                    className="text-success"
                    size="xs"
                  />
                )
              )}
            </button>
          </Tooltipable>
        )}
      </div>
    );
  },
);

export default NotificationItem;
